<template>
  <v-modal v-model="showModal" size="full" z-index="100000" :noPadding="true" modal-title="İçerik İyileştirme Önerisi" modal-description="Mevcut içeriği inceleyerek iyileştirme önerinizi oluşturun.">
    <!-- Modal başlık ikonu -->
    <template #icon>
      <span class="material-symbols-outlined text-primary text-2xl">auto_fix_high</span>
    </template>

    <div class="flex flex-col h-full">
      <!-- Responsive İçerik Alanı - Masaüstünde iki sütun, Mobilde tablar -->
      <div class="flex flex-col md:flex-row flex-grow overflow-hidden" style="height: calc(100% - 160px);">
        <!-- Tab Navigation (Sadece Mobilde Görünür) -->
        <div class="md:hidden flex border-b border-gray-200 bg-gray-50">
          <button @click="activeTab = 'content'" class="flex-1 py-3 px-4 text-sm font-medium transition-colors duration-200" :class="activeTab === 'content' ? 'text-primary border-b-2 border-primary bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'">
            <span class="flex items-center justify-center">
              <span class="material-symbols-outlined mr-1">visibility</span>
              Mevcut İçerik
            </span>
          </button>
          <button @click="activeTab = 'suggestion'" class="flex-1 py-3 px-4 text-sm font-medium transition-colors duration-200" :class="activeTab === 'suggestion' ? 'text-primary border-b-2 border-primary bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'">
            <span class="flex items-center justify-center">
              <span class="material-symbols-outlined mr-1">edit_note</span>
              Öneri Detayları
            </span>
          </button>
        </div>

        <!-- Sol Sütun: Mevcut İçerik Görüntüleme -->
        <div class="w-full md:w-1/2 overflow-y-auto p-4 md:p-6 bg-gray-50 pb-20 thin-scrollbar" v-show="activeTab === 'content' || isDesktop">
          <div class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-blue-50 border-b border-gray-200 flex items-center justify-between">
              <h3 class="font-medium text-gray-800 flex items-center">
                <span class="material-symbols-outlined text-blue-600 mr-2">visibility</span> Mevcut İçerik </h3>
              <span class="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                {{ getContentTypeLabel(contentType) }}
              </span>
            </div>

            <div class="p-5">
              <!-- İçerik Tipi: TEXT -->
              <div v-if="contentType === 'TEXT'" class="prose max-w-none">
                <div class="bg-gray-50 p-5 rounded-lg border border-gray-200" v-if="currentText">
                  <div class="text-sm" v-html="currentText"></div>
                </div>
                <div v-else class="text-center py-10 text-gray-400">
                  <span class="material-symbols-outlined text-4xl mb-2">description</span>
                  <p>İçerik metni bulunamadı</p>
                </div>
              </div>

              <!-- İçerik Tipi: IMAGE -->
              <div v-if="contentType === 'IMAGE'" class="flex flex-col items-center py-4">
                <div class="bg-gray-50 p-3 rounded-lg border border-gray-200" v-if="currentImageUrl">
                  <img :src="currentImageUrl" alt="Mevcut Görsel" class="max-h-96 object-contain rounded"/>
                </div>
                <div v-else class="text-center py-10 text-gray-400">
                  <span class="material-symbols-outlined text-4xl mb-2">image</span>
                  <p>Görsel bulunamadı</p>
                </div>
              </div>

              <!-- İçerik Tipi: VIDEO -->
              <div v-if="contentType === 'VIDEO'" class="flex flex-col py-4">
                <div v-if="currentVideoUrl">
                  <div class="text-sm font-medium text-gray-700 mb-2">Video URL:</div>
                  <div class="flex bg-gray-50 border border-gray-200 rounded overflow-hidden">
                    <input type="text" :value="currentVideoUrl" readonly class="flex-grow p-3 bg-transparent text-gray-800"/>
                    <a :href="currentVideoUrl" target="_blank" class="bg-primary text-white p-3 flex items-center"> <span class="material-symbols-outlined">open_in_new</span> </a>
                  </div>
                </div>
                <div v-else class="text-center py-10 text-gray-400">
                  <span class="material-symbols-outlined text-4xl mb-2">videocam</span>
                  <p>Video bulunamadı</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Sol taraftaki ipuçları kaldırıldı -->
        </div>

        <!-- Sağ Sütun: Öneri Formu -->
        <div class="w-full md:w-1/2 overflow-y-auto p-4 md:p-6 border-l-0 md:border-l border-gray-200 pb-20" v-show="activeTab === 'suggestion' || isDesktop">
          <!-- Öneri Formu -->
          <div class="space-y-3">
            <div class="bg-white rounded-lg shadow-sm p-5 border border-primary border-opacity-20">
              <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2">
                <div class="flex items-center">
                  <span class="material-symbols-outlined text-primary mr-2">edit_note</span>
                  <h3 class="font-medium text-gray-800">Öneri Detayları</h3>
                </div>
                <span class="text-xs px-2 py-1 bg-primary bg-opacity-10 text-primary rounded-full self-start sm:self-auto">
                  {{ getContentCategoryLabel(contentCategory) }}
                </span>
              </div>

              <!-- Öneri Metni -->
              <div class="mb-5">
                <div class="flex justify-between mb-2">
                  <label class="block text-sm font-semibold text-gray-700"> Öneri Detayı <span class="text-red-500">*</span> </label> <span class="text-xs text-gray-500">
                    <span :class="{'text-red-500': form.suggestion.length > 500, 'text-green-600': form.suggestion.length > 0 && form.suggestion.length <= 500}">{{ form.suggestion.length }}</span> / 500
                  </span>
                </div>

                <v-input v-model="form.suggestion" type="textarea" :placeholder="suggestPlaceholder" :error="errors.suggestion" textAreaHeight="h-36" required class="transition-all duration-200 focus-within:shadow-sm"/>
              </div>

              <!-- Öncelik Ayarı -->
              <div class="bg-gray-50 rounded-lg p-4 mb-5">
                <div class="flex justify-between items-center mb-3">
                  <label class="text-sm font-semibold text-gray-700 flex items-center"> <span class="material-symbols-outlined text-amber-500 mr-1 !text-sm">priority_high</span> Öneri Önceliği
                  </label> <span class="text-xs font-medium px-2 py-1 rounded-full" :class="{
                      'bg-green-100 text-green-800': form.priority <= 2,
                      'bg-yellow-100 text-yellow-800': form.priority === 3,
                      'bg-red-100 text-red-800': form.priority >= 4
                    }">
                    {{ ['Çok Düşük', 'Düşük', 'Orta', 'Yüksek', 'Çok Yüksek'][form.priority - 1] }}
                  </span>
                </div>

                <div class="flex flex-col sm:flex-row sm:justify-between gap-4">
                  <div class="flex items-center gap-1 md:gap-2 justify-center sm:justify-start">
                    <button v-for="priority in [1, 2, 3, 4, 5]" :key="priority" type="button" @click="form.priority = priority" class="w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-xs md:text-sm transition-all hover:transform hover:scale-110" :class="{
                'bg-green-100 text-green-800 ring-2 ring-green-400 shadow-sm': form.priority === priority && priority <= 2,
                'bg-yellow-100 text-yellow-800 ring-2 ring-yellow-400 shadow-sm': form.priority === priority && priority === 3,
                'bg-red-100 text-red-800 ring-2 ring-red-400 shadow-sm': form.priority === priority && priority >= 4,
                'bg-gray-100 text-gray-600 hover:bg-gray-200': form.priority !== priority
                }">
                      {{ priority }}
                    </button>
                  </div>

                  <div class="w-full sm:w-40">
                    <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div class="h-full transition-all duration-300" :class="{
                'bg-green-500': form.priority <= 2,
                'bg-yellow-500': form.priority === 3,
                'bg-red-500': form.priority >= 4
                }" :style="{width: (form.priority * 20) + '%'}"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- İçerik Tipine Özel Alanlar -->
            <div v-if="contentType === 'TEXT'" class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div class="flex items-center mb-3">
                <span class="material-symbols-outlined text-green-600 mr-2">text_format</span>
                <h3 class="font-medium text-gray-800">İyileştirilmiş Metin</h3>
              </div>

              <div class="flex justify-end mb-2">
                <span class="text-xs text-gray-500">
                  {{ form.currentText ? form.currentText.length : 0 }} karakter
                </span>
              </div>

              <v-input v-model="form.currentText" type="textarea" placeholder="Mevcut içerikten kopyaladığınız metni burada düzenleyebilir veya yeni metni doğrudan yazabilirsiniz..." textAreaHeight="h-40" class="transition-all duration-200 focus-within:shadow-sm"/>
            </div>

            <div v-if="contentType === 'IMAGE'" class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div class="flex items-center mb-4">
                <span class="material-symbols-outlined text-blue-600 mr-2">image</span>
                <h3 class="font-medium text-gray-800">Önerilen Görsel</h3>
              </div>

              <label class="block text-sm font-medium text-gray-700 mb-2">Önerilen Görsel URL'i</label>
              <v-input v-model.trim="form.currentImageUrl" :error="errors.currentImageUrl" placeholder="https://example.com/iyilestirilmis-gorsel.jpg" showIcon icon="image" class="transition-all duration-200 focus-within:shadow-sm"/>
              <p class="text-xs text-gray-500 mt-2 flex items-start">
                <span class="material-symbols-outlined !text-xs mr-1 mt-0.5">info</span> <span>Görsel URL'i <b>http://</b> veya <b>https://</b> ile başlamalıdır.</span>
              </p>
            </div>

            <div v-if="contentType === 'VIDEO'" class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div class="flex items-center mb-4">
                <span class="material-symbols-outlined text-red-600 mr-2">videocam</span>
                <h3 class="font-medium text-gray-800">Önerilen Video</h3>
              </div>

              <label class="block text-sm font-medium text-gray-700 mb-2">Önerilen Video URL'i</label>
              <v-input v-model="form.currentVideoUrl" :error="errors.currentVideoUrl" placeholder="https://example.com/iyilestirilmis-video.mp4" showIcon icon="videocam" class="transition-all duration-200 focus-within:shadow-sm"/>
              <p class="text-xs text-gray-500 mt-2 flex items-start">
                <span class="material-symbols-outlined !text-xs mr-1 mt-0.5">info</span> <span>YouTube, Vimeo veya diğer video paylaşım platformlarının linklerini kullanabilirsiniz.</span>
              </p>
            </div>

            <!-- İyileştirme İpuçları -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <!-- Etkili Öneri Oluşturma Kutusu -->
              <div class="bg-indigo-50 rounded-lg shadow-sm border border-indigo-100 p-4">
                <div class="flex items-center mb-3">
                  <span class="material-symbols-outlined text-indigo-600 mr-2">lightbulb</span>
                  <h3 class="text-sm font-semibold text-gray-800">Etkili Öneri Nasıl Oluşturulur?</h3>
                </div>

                <ul class="text-xs text-gray-600 space-y-2 list-disc pl-5">
                  <li>Önerinizi <span class="font-medium text-indigo-700">spesifik, ölçülebilir ve uygulanabilir</span> yapın</li>
                  <li>Nedenler ve beklenen sonuçları <span class="font-medium text-indigo-700">açıkça belirtin</span></li>
                  <li>Öneri önceliğini içeriğin önemine göre belirleyin</li>
                  <li>Somut ve uygulanabilir öneriler sunun</li>
                </ul>
              </div>

              <!-- Metin, Görsel veya Video İpuçları -->
              <div v-if="contentType === 'TEXT'" class="bg-green-50 rounded-lg shadow-sm border border-green-100 p-4">
                <div class="flex items-center mb-3">
                  <span class="material-symbols-outlined text-green-600 mr-2">text_format</span>
                  <h3 class="text-sm font-semibold text-gray-800">Metin İçerik İpuçları</h3>
                </div>

                <ul class="text-xs text-gray-600 space-y-2 list-disc pl-5">
                  <li>Dilbilgisi ve yazım hatalarına dikkat edin</li>
                  <li>Anahtar kelimelerin doğru kullanıldığından emin olun</li>
                  <li>Metin içeriği hedef kitleye uygun ve anlaşılır mı?</li>
                  <li>Metnin akışı ve yapısı okumayı kolaylaştırıyor mu?</li>
                </ul>
              </div>

              <div v-if="contentType === 'IMAGE'" class="bg-blue-50 rounded-lg shadow-sm border border-blue-100 p-4">
                <div class="flex items-center mb-3">
                  <span class="material-symbols-outlined text-blue-600 mr-2">image</span>
                  <h3 class="text-sm font-semibold text-gray-800">Görsel İçerik İpuçları</h3>
                </div>

                <ul class="text-xs text-gray-600 space-y-2 list-disc pl-5">
                  <li>Görselin çözünürlüğü hedef platform için uygun mu?</li>
                  <li>Görseldeki konu net ve odakta mı?</li>
                  <li>Renk ve kontrast ayarlamaları gerekli mi?</li>
                  <li>Görsel, hedef kitleye uygun ve dikkat çekici mi?</li>
                </ul>
              </div>

              <div v-if="contentType === 'VIDEO'" class="bg-red-50 rounded-lg shadow-sm border border-red-100 p-4">
                <div class="flex items-center mb-3">
                  <span class="material-symbols-outlined text-red-600 mr-2">videocam</span>
                  <h3 class="text-sm font-semibold text-gray-800">Video İçerik İpuçları</h3>
                </div>

                <ul class="text-xs text-gray-600 space-y-2 list-disc pl-5">
                  <li>Video çözünürlüğü ve kalitesi yeterli mi?</li>
                  <li>İçerik hedef platform için uygun formatta mı?</li>
                  <li>Video uzunluğu hedef kitle için ideal mi?</li>
                  <li>Alternatif bir video içeriği daha etkili olabilir mi?</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer - Sabit -->
      <div class="border-t border-gray-200 bg-gray-50 p-3 md:p-4 flex justify-between items-center absolute bottom-0 left-0 right-0 z-10 shadow-md">
        <p class="text-xs text-gray-600 flex items-center">
          <span class="material-symbols-outlined text-amber-500 mr-1">lightbulb</span> Öneriyi göndermeden önce tüm alanları kontrol edin. </p>

        <div class="flex gap-2 md:gap-3">
          <v-button @click="closeModal" button-text="Vazgeç" color="secondary" type="button" icon="close" size="sm" :disabled="isSubmitting" class="hover:shadow-sm"/>
          <v-button @click="handleSubmit" button-text="Öneri Gönder" color="primary" :is-loading="isSubmitting" loading-text="Gönderiliyor..." type="button" icon="send" :disabled="!isFormValid || isSubmitting" size="sm" class="hover:shadow-sm shadow-xs"/>
        </div>
      </div>
    </div>
  </v-modal>
</template>

<script setup>
import { computed, watch, reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import VModal from '@/components/modals/VModal.vue'
import VButton from '@/components/base/buttons/VButton.vue'
import VInput from '@/components/base/inputs/VInput.vue'
import VAccordion from '@/components/common/VAccordion.vue'
import { useSuggestionsManagement, useSuggestionCreate } from '../../composables'
import { getContentTypeLabel, getContentCategoryLabel, getContentTypeIcon, CONTENT_TYPES } from '../../constants/suggestions'

// Mobil tab yönetimi için
const activeTab = ref('content') // 'content' veya 'suggestion'

// Desktop için responsive kontrol
const isDesktop = ref(false)
const checkScreenSize = () => {
  isDesktop.value = window.innerWidth >= 768 // md breakpoint genellikle 768px'dir
}

// Component yüklenirken ve pencere boyutu değiştiğinde screen size kontrolü
onMounted(() => {
  checkScreenSize()
  window.addEventListener('resize', checkScreenSize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenSize)
})

// Composable'ları kullan
const { createSuggestion } = useSuggestionsManagement()
const {
  form,
  errors,
  isSubmitting,
  isFormValid,
  submitForm,
  resetForm,
  initForm,
} = useSuggestionCreate()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  contentId: {
    type: [String, Number],
    required: true,
  },
  contentModel: {
    type: String,
    required: true,
    validator: (value) => ['Image', 'TextContent', 'Video'].includes(value),
  },
  contentType: {
    type: String,
    required: true,
    validator: (value) => Object.values(CONTENT_TYPES).includes(value),
  },
  contentCategory: {
    type: String,
    required: true,
    validator: (value) => ['SOCIAL_MEDIA', 'BLOG', 'ASSISTANT'].includes(value),
  },
  currentText: {
    type: String,
    default: '',
  },
  currentImageUrl: {
    type: String,
    default: '',
  },
  currentVideoUrl: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'suggestion-created'])

const showModal = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// İçerik tipine göre öneri alanı placeholder metni
const suggestPlaceholder = computed(() => {
  if (props.contentType === 'TEXT' && props.contentCategory === 'BLOG') {
    return `İyileştirilecek bölüm: [hangi bölümün iyileştirileceğini belirtin]
Önerilen değişiklikler: [değişiklik önerilerinizi buraya yazın]
Nedenler: [bu değişikliği önerme nedeninizi açıklayın]
Beklenen sonuç: [bu değişikliklerle elde edilecek faydaları açıklayın]`
  } else {
    return `Önerilen değişiklikler: [değişiklik önerilerinizi buraya yazın]
Nedenler: [bu değişikliği önerme nedeninizi açıklayın]
Beklenen sonuç: [bu değişikliklerle elde edilecek faydaları açıklayın]`
  }
})

// Form gönderim fonksiyonu
const handleSubmit = async () => {
  await submitForm(
      props,   // İçerik özellikleri
      createSuggestion,  // Composable'dan gelen createSuggestion fonksiyonu
      // Başarı durumunda - event emit
      (response) => emit('suggestion-created', response),
      // Modalı kapat
      () => showModal.value = false,
      // Form sıfırlama
      resetForm,
  )
}

// Modalı kapatma fonksiyonu
const closeModal = () => {
  if (isSubmitting.value) return

  // Formu temizle ve modalı kapat
  resetForm()
  showModal.value = false
}

// Prop değişikliklerini izle
watch(() => props.currentImageUrl, (newVal) => {
  if (props.contentType === CONTENT_TYPES.IMAGE) {
    form.currentImageUrl = newVal
    errors.currentImageUrl = ''
  }
})

watch(() => props.currentVideoUrl, (newVal) => {
  if (props.contentType === CONTENT_TYPES.VIDEO) {
    form.currentVideoUrl = newVal
    errors.currentVideoUrl = ''
  }
})

watch(() => showModal.value, (isOpen) => {
  if (isOpen) {
    // Modal açıldığında form içeriğini sıfırla
    initForm(props)
  }
})
</script>